// src/Component/MarketSection.tsx
import React from 'react';
import { motion } from 'framer-motion';
import vdo from '../assets/main.mp4';
import { Link } from 'react-router-dom';

const MarketSection: React.FC = () => {
  // Smooth animation variants for text stagger effect
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] } 
    },
  };

  // Ultra smooth spring-like animation for the video
  const videoVariants = {
    hidden: { opacity: 0, scale: 0.85, x: 50 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      x: 0, 
      transition: { duration: 1.2, ease: [0.16, 1, 0.3, 1] } 
    },
  };

  return (
    <section className="relative w-full py-16 md:py-24 bg-slate-50 overflow-hidden flex flex-col justify-center font-[Urbanist]">
      
      {/* 1. SUBTLE BACKGROUND GLOW EFFECTS */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[400px] md:w-[600px] h-[400px] md:h-[600px] bg-blue-400/15 rounded-full blur-[120px] md:blur-[150px]" />
        <div className="absolute bottom-0 left-0 translate-y-1/4 -translate-x-1/4 w-[400px] md:w-[600px] h-[400px] md:h-[600px] bg-indigo-400/15 rounded-full blur-[120px] md:blur-[150px]" />
      </div>

      {/* 2. MAIN CONTAINER - Increased max-width and side padding for better spacing */}
      <div className="max-w-[90rem] mx-auto px-6 sm:px-8 md:px-12 lg:px-16 relative z-10 w-full">
        
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-16 xl:gap-20">
          
          {/* LEFT CONTENT (Width reduced slightly to give video more space) */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            className="w-full lg:w-[45%] xl:w-[40%] flex flex-col items-start text-left space-y-6 md:space-y-8"
          >
            <motion.div  className="space-y-4">
              <span className="inline-block py-1.5 px-4 rounded-full bg-green-100/80 text-green-700 text-sm font-bold tracking-wide shadow-sm border border-green-200/50">
                Smart Trading Platform
              </span>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-slate-900 tracking-tight leading-[1.1]">
                Where the world <br className="hidden lg:block"/> does markets.
              </h2>
            </motion.div>
            
            <motion.div >
              <p className="text-base sm:text-lg lg:text-xl text-slate-600 max-w-xl font-medium leading-relaxed">
                Join millions of traders and investors taking the future into their own hands. Experience lightning-fast execution and real-time market analytics.
              </p>
            </motion.div>

            {/* Feature List */}
            <motion.ul  className="space-y-4 w-full">
              {[
                'Real-time live market data widgets',
                'Advanced charting and algo visualizations',
                'Zero-latency execution and high reliability'
              ].map((feature, index) => (
                <li key={index} className="flex items-center gap-3 text-slate-700 font-bold text-sm sm:text-base">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center shadow-sm border border-emerald-200/50">
                    <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  {feature}
                </li>
              ))}
            </motion.ul>
            
            <Link to="/demat/register" className="w-full md:w-auto">
              <motion.div >
                <button className="mt-4 px-8 py-4 bg-slate-900 text-white rounded-xl font-bold text-lg shadow-2xl shadow-slate-900/30 hover:bg-slate-800 hover:-translate-y-1 transition-all duration-300">
                  Start Trading Now
                </button>
              </motion.div>
            </Link>
          </motion.div>

          {/* RIGHT VIDEO SIDE (Width increased to make video bigger) */}
          <motion.div
            // variants={videoVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            className="w-full lg:w-[55%] xl:w-[60%] relative"
          >
            {/* Glowing Shadow Wrapper */}
            <div className="relative rounded-2xl md:rounded-[2rem] bg-white border border-slate-200/60 p-2 md:p-3 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.15)]">
              
              {/* The Video Window */}
              <div className="bg-[#0b1215] rounded-xl md:rounded-[1.5rem] overflow-hidden relative aspect-video group">
                
                {/* Fake Browser Header (Mac style UI) */}
                <div className="absolute top-0 left-0 w-full h-10 md:h-12 bg-slate-900/40 backdrop-blur-md border-b border-white/10 flex items-center px-4 md:px-5 gap-2.5 z-20 transition-opacity duration-300">
                  <div className="w-3 h-3 md:w-3.5 md:h-3.5 rounded-full bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.6)]" />
                  <div className="w-3 h-3 md:w-3.5 md:h-3.5 rounded-full bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.6)]" />
                  <div className="w-3 h-3 md:w-3.5 md:h-3.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)]" />
                </div>

                {/* VIDEO TAG */}
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                >
                  <source 
                    src={vdo}
                    type="video/mp4" 
                  />
                  Your browser does not support the video tag.
                </video>

                {/* Overlay Gradient (Bottom fade) */}
                <div className="absolute bottom-0 left-0 w-full h-20 md:h-32 bg-gradient-to-t from-slate-900/90 to-transparent pointer-events-none" />
              </div>
            </div>

            {/* Floating UI Badge 1 (BTC/USD) - With entrance + continuous motion */}
            <motion.div 
              initial={{ opacity: 0, scale: 0, y: 20 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6, type: "spring", bounce: 0.4 }}
              viewport={{ once: true }}
              className="absolute -bottom-6 -left-4 md:-bottom-10 md:-left-10 z-30"
            >
              <motion.div 
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="bg-white/80 backdrop-blur-xl border border-white p-3 md:p-5 rounded-2xl shadow-2xl flex items-center gap-3 md:gap-4"
              >
                <div className="w-3 h-3 md:w-4 md:h-4 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_12px_rgba(16,185,129,0.9)]" />
                <div className="flex flex-col">
                  <span className="text-sm md:text-base font-extrabold text-slate-900 tracking-wider">BTC/USD</span>
                  <span className="text-[10px] md:text-sm font-semibold text-slate-500">Live Market</span>
                </div>
              </motion.div>
            </motion.div>
            
            {/* Floating UI Badge 2 (High Liquidity) */}
            <motion.div 
              initial={{ opacity: 0, scale: 0, y: -20 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6, type: "spring", bounce: 0.4 }}
              viewport={{ once: true }}
              className="absolute -top-6 -right-4 md:-top-8 md:-right-8 z-30"
            >
              <motion.div 
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="bg-white/80 backdrop-blur-xl border border-white p-3 md:p-4 rounded-xl shadow-2xl"
              >
                <span className="text-xs md:text-sm font-bold text-green-700 tracking-wider">High Liquidity</span>
              </motion.div>
            </motion.div>

          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default MarketSection;